import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import BoltIcon from "@mui/icons-material/Bolt";
import PageHeader from "../../components/common/PageHeader";
import FilterBar from "../../components/common/FilterBar";
import EmptyState from "../../components/common/EmptyState";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";

import {
  useGenerateSeriesMutation,
  useListSeriesQuery,
  usePauseSeriesMutation,
  useResumeSeriesMutation,
} from "../../features/transfers/seriesApi";
import { useListClientsQuery, useListProvidersQuery } from "../../features/masterdata/masterdataApi";
import type { SeriesStatus } from "../../features/transfers/types";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { usePersistedListFilters } from "../../hooks/usePersistedListFilters";

const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
const maskToWeekdays = (mask: number) =>
  WEEKDAYS.filter((d, idx) => ((mask >> idx) & 1) === 1);

type SeriesOrdering = "-created_at" | "created_at" | "start_date" | "-start_date";

export default function SeriesListPage() {
  const nav = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [filters, setFilters] = usePersistedListFilters("series", {
    status: "" as SeriesStatus | "",
    frequency: "" as "" | "daily" | "weekly",
    client: "",
    defaultProvider: "",
    search: "",
    ordering: "-created_at" as SeriesOrdering,
  });
  const { status, frequency, client, defaultProvider, search, ordering } = filters;
  const debouncedSearch = useDebouncedValue(search);

  const [page, setPage] = useState(1);

  const toggleSort = (field: "start_date" | "created_at") => {
    setFilters((prev) => ({
      ...prev,
      ordering: (prev.ordering === field ? `-${field}` : prev.ordering === `-${field}` ? field : field) as SeriesOrdering,
    }));
  };
  const sortIcon = (field: "start_date" | "created_at") => {
    if (ordering === field) return " ↑";
    if (ordering === `-${field}`) return " ↓";
    return " ⇅";
  };

  const clientsQ = useListClientsQuery({ page: 1, page_size: 200, ordering: "name" });
  const providersQ = useListProvidersQuery({ page: 1, page_size: 200, ordering: "name" });

  useEffect(() => {
    setPage(1);
  }, [status, frequency, client, defaultProvider, debouncedSearch, ordering]);

  const { data, isLoading, isFetching, error } = useListSeriesQuery({
    page,
    status: status || undefined,
    frequency: frequency || undefined,
    client: client || undefined,
    default_provider: defaultProvider || undefined,
    search: debouncedSearch,
    ordering,
  });

  const rows = data?.results ?? [];

  // Generate modal
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateUntil, setGenerateUntil] = useState("");
  const [selectedSeriesId, setSelectedSeriesId] = useState<string | null>(null);

  const [generateSeries, { isLoading: isGenerating }] = useGenerateSeriesMutation();
  const [pauseSeries, { isLoading: isPausing }] = usePauseSeriesMutation();
  const [resumeSeries, { isLoading: isResuming }] = useResumeSeriesMutation();

  const openGenerate = (seriesId: string) => {
    setSelectedSeriesId(seriesId);
    setGenerateUntil("");
    setGenerateOpen(true);
  };

  const onGenerate = async () => {
    if (!selectedSeriesId) return;
    try {
      const res = await generateSeries({
        id: selectedSeriesId,
        generate_until: generateUntil || null,
      }).unwrap();

      enqueueSnackbar(`Created ${res.created} transfers`, { variant: "success" });
      setGenerateOpen(false);
    } catch (e: any) {
      enqueueSnackbar(e?.data?.detail || "Failed to generate", { variant: "error" });
    }
  };

  const onPauseResume = async (seriesId: string, next: "paused" | "active") => {
    try {
      if (next === "paused") {
        await pauseSeries({ id: seriesId }).unwrap();
        enqueueSnackbar("Series paused", { variant: "success" });
      } else {
        await resumeSeries({ id: seriesId }).unwrap();
        enqueueSnackbar("Series resumed", { variant: "success" });
      }
    } catch (e: any) {
      enqueueSnackbar(e?.data?.detail || "Failed", { variant: "error" });
    }
  };

  const showEmpty = !isLoading && rows.length === 0;

  const canPrev = !!data?.previous && page > 1;
  const canNext = !!data?.next;

  return (
    <Box>
      <PageHeader
        title="Series"
        subtitle="Recurring schedules that generate transfers"
        actions={
          <Button variant="contained" onClick={() => nav("/app/series/new")}>
            New Series
          </Button>
        }
      />

      <FilterBar loading={isLoading || isFetching}>
        <TextField
          label="Status"
          select
          value={status}
          onChange={(e) => setFilters({ status: e.target.value as SeriesStatus | "" })}
          size="small"
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="paused">Paused</MenuItem>
          <MenuItem value="archived">Archived</MenuItem>
        </TextField>
        <TextField
          label="Frequency"
          select
          value={frequency}
          onChange={(e) => setFilters({ frequency: e.target.value as "" | "daily" | "weekly" })}
          size="small"
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="daily">Daily</MenuItem>
          <MenuItem value="weekly">Weekly</MenuItem>
        </TextField>
        <TextField
          label="Client"
          select
          value={client}
          onChange={(e) => setFilters({ client: e.target.value })}
          size="small"
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          {(clientsQ.data?.results ?? []).map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Default provider"
          select
          value={defaultProvider}
          onChange={(e) => setFilters({ defaultProvider: e.target.value })}
          size="small"
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          {(providersQ.data?.results ?? []).map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Search (title)"
          value={search}
          onChange={(e) => setFilters({ search: e.target.value })}
          size="small"
          placeholder="Search…"
          sx={{ minWidth: 180, flex: 1 }}
        />
      </FilterBar>

      {error ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          Failed to load series.
        </Paper>
      ) : showEmpty ? (
        <EmptyState
          title="No series yet"
          description="Create a recurring program (e.g. Mon/Wed/Fri) and generate transfers."
          actionLabel="Create series"
          onAction={() => nav("/app/series/new")}
        />
      ) : (
        <>
          <Paper variant="outlined" sx={{ overflow: "auto", width: "100%" }}>
            <Table sx={{ minWidth: 520 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>
                    <Box
                      component="span"
                      onClick={() => toggleSort("start_date")}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "pointer",
                        fontWeight: 600,
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      Dates{sortIcon("start_date")}
                    </Box>
                  </TableCell>
                  <TableCell>Rule</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((r: any) => {
                  const rule =
                    r.frequency === "weekly"
                      ? `weekly • every ${r.interval} • ${maskToWeekdays(r.days_of_week_mask ?? 0).join(", ")}`
                      : `daily • every ${r.interval}`;

                  return (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{r.title}</TableCell>
                      <TableCell>
                        {r.start_date} → {r.end_date}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={rule} />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={r.status} />
                      </TableCell>

                      <TableCell align="right">
                        <Tooltip title="Generate transfers">
                          <IconButton size="small" onClick={() => openGenerate(r.id)}>
                            <BoltIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {r.status === "active" ? (
                          <Tooltip title="Pause">
                            <IconButton
                              size="small"
                              onClick={() => onPauseResume(r.id, "paused")}
                              disabled={isPausing || isResuming}
                            >
                              <PauseCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Resume">
                            <IconButton
                              size="small"
                              onClick={() => onPauseResume(r.id, "active")}
                              disabled={isPausing || isResuming}
                            >
                              <PlayCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* ✅ fix route */}
                        <Button size="small" onClick={() => nav(`/app/series/${r.id}/edit`)}>
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>

          {/* paging footer */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 2 }}
          >
            <div />
            <Stack direction="row" spacing={1}>
              <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={isFetching || !canPrev}>
                Prev
              </Button>
              <Button onClick={() => setPage((p) => p + 1)} disabled={isFetching || !canNext}>
                Next
              </Button>
            </Stack>
          </Stack>
        </>
      )}

      {/* Generate modal */}
      <Dialog open={generateOpen} onClose={() => setGenerateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Generate transfers</DialogTitle>
        <DialogContent>
          <TextField
            label="Generate until"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={generateUntil}
            onChange={(e) => setGenerateUntil(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onGenerate} disabled={!selectedSeriesId || isGenerating}>
            {isGenerating ? "Generating..." : "Generate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
